<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import { usePermissionStore } from "@/stores/usePermissionStore";
import CustomTextarea from "../Generics/CustomTextarea.vue";

import { useDraggableModal } from "@/composables/useDraggableModal";

const permissionStore = usePermissionStore();
const emit = defineEmits(["close", "refresh"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const formData = ref({
  permissionName: "",
  description: "",
  referencedTable: "", // ✅ NOVO
});

const errors = ref({});
const isSubmitting = ref(false);

// ====================================================================
// ✅ TOAST PADRÃO ADDUSERGROUPFORM.VUE (3000ms) 🚀
// (Copiado de AddActivityForm.vue para consistência)
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null; // Limpa a referência
  }, 3000); // 3 segundos, padrão AddUserGroupForm
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}
// ====================================================================

const permissionName = ref(null);

function validateForm() {
  errors.value = {};
  if (
    !formData.value.permissionName ||
    formData.value.permissionName.trim().length < 3
  ) {
    errors.value.permissionName =
      "O nome da permissão deve ter pelo menos 3 caracteres.";
  }
  return Object.keys(errors.value).length === 0;
}

async function handleSubmit() {
  if (isSubmitting.value) return;

  if (!validateForm()) {
    showErrorToast("Verifique os campos obrigatórios antes de salvar.");
    return;
  }

  isSubmitting.value = true;

  try {
    const payload = {
      permissionName: formData.value.permissionName,
      description: formData.value.description,
      referencedTable: formData.value.referencedTable || null, // ✅ NOVO
    };

    await permissionStore.createPermission(payload);

    // ✅ TOAST DE SUCESSO (usa a função padronizada de 3s)
    showSuccessToast("Permissão criada com sucesso.");

    // ✅ fecha modal depois de 2.4s (padrão AddActivityForm.vue)
    setTimeout(() => {
      emit("refresh");
      emit("close");
      isSubmitting.value = false;
    }, 2400);
  } catch (error) {
    console.error("❌ Erro no formulário de adição de permissão:", error);

    let userFriendlyMessage =
      error?.message || "Ocorreu um erro ao criar a permissão.";

    if (error?.response?.data?.errors) {
      const messages = Object.values(error.response.data.errors).flat();
      if (messages.length > 0) userFriendlyMessage = messages.join("\n");
    } else if (error?.response?.data?.message) {
      userFriendlyMessage = error.response.data.message;
    }

    // ✅ TOAST DE ERRO (usa a função padronizada de 3s)
    showErrorToast(userFriendlyMessage);
    isSubmitting.value = false;
  }
}

function closeModal() {
  if (!isSubmitting.value) {
    emit("close");
  }
}

function handleEscKey(event) {
  if (event.key === "Escape") {
    closeModal();
  }
}

onMounted(() => {
  nextTick(() => {
    if (permissionName.value) permissionName.value.focus();
  });

  document.addEventListener("keydown", handleEscKey);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});
</script>

<template>
  <div class="modal-backdrop" @click.self="!isSubmitting">
    <div ref="panelRef" class="modal-panel small" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h2 class="modal-title">Adicionar Nova Permissão</h2>
        <button
          @click="closeModal"
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <form @submit.prevent="handleSubmit" novalidate>
        <div class="modal-body">
          <div class="form-column">
            <!-- Nome da Permissão -->
            <div class="form-group">
              <label for="permissionName" class="form-label">
                Nome da Permissão
              </label>

              <input
                ref="permissionName"
                id="permissionName"
                type="text"
                v-model="formData.permissionName"
                class="input-base"
                :class="{ 'is-invalid': errors.permissionName }"
                required
                @focus="$event.target.select()"
              />

              <span v-if="errors.permissionName" class="error-message">
                {{ errors.permissionName }}
              </span>
            </div>

            <!-- ✅ Tabela Referenciada -->
            <div class="form-group">
              <label for="referencedTable" class="form-label">
                Tabela Referenciada (opcional)
              </label>

              <input
                id="referencedTable"
                type="text"
                v-model="formData.referencedTable"
                class="input-base"
                placeholder="Ex: Texts, Notes, Activities"
              />
            </div>

            <!-- Descrição -->
            <div class="form-group">
              <label for="description" class="form-label">
                Descrição (opcional)
              </label>

              <CustomTextarea
                v-model:body="formData.description"
                placeholder="Digite uma descrição (opcional)"
                :is-disabled="false"
              />
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            @click="closeModal"
            class="btn btn-secondary"
            :disabled="isSubmitting"
          >
            Cancelar
          </button>

          <button
            type="submit"
            class="btn btn-success"
            :disabled="isSubmitting"
          >
            <svg
              v-if="isSubmitting"
              class="w-5 h-5 mr-2 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>

            <svg
              v-else
              class="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clip-rule="evenodd"
              />
            </svg>

            <span>
              {{ isSubmitting ? "Adicionando..." : "Adicionar Permissão" }}
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- TOAST -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.form-column {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.error-message {
  color: var(--text-important);
  font-size: 0.85rem;
  margin-top: 0.15rem;
}

</style>
